// Canonical `Parameters` contract — mirrors ARCHITECTURE.md v4 + v5 expansions.
// Every field on `Parameters` is exposed as a UI control in
// `ControlsPanel` (see `src/components/ControlsPanel.tsx`) AND is a
// valid LLM-output target for the chat translator. Defaults below
// match the JSON defaults shipped in ARCHITECTURE.md and the v5
// backend zod schema in `backend/src/validation/index.ts`.

export type IrisPattern = 'radial' | 'cryptic' | 'zonular' | 'mixed';
export type PupilShape = 'round' | 'slightly-off-round';
export type EyelidShape = 'open' | 'half' | 'closed';
export type LashCurve = 'straight' | 'subtle' | 'dramatic';
export type CatchlightShape = 'round' | 'oval' | 'square' | 'star';
export type CatchlightCountPattern = 'single' | 'paired' | 'cluster';
export type ReflectedScenePreset =
  | 'sky'
  | 'fire'
  | 'forest'
  | 'water'
  | 'stars'
  | 'city'
  | 'abstract';
export type AspectRatio = '1:1' | '4:3' | '3:4' | '16:9' | '9:16';
export type Resolution = '1K' | '2K' | '4K';
export type NVariations = 1 | 4;

/* ------------------------------------------------------------------ *
 * v5 — emotional / directional state                                  *
 * ------------------------------------------------------------------ */

export type Emotion =
  | 'neutral'
  | 'alert'
  | 'menacing'
  | 'sad'
  | 'sleepy'
  | 'surprised'
  | 'contempt'
  | 'fierce'
  | 'tearful'
  | 'peaceful'
  | 'haunted';

export type GazeDirection =
  | 'centre'
  | 'left'
  | 'right'
  | 'up'
  | 'down'
  | 'up-left'
  | 'up-right'
  | 'down-left'
  | 'down-right';

export type LightingKeyAngle =
  | 'front-left'
  | 'front-right'
  | 'side-left'
  | 'side-right'
  | 'back-left'
  | 'back-right'
  | 'above'
  | 'below'
  | 'ambient';

export type LightingQuality = 'hard' | 'soft';
export type LightingTemperature = 'warm' | 'neutral' | 'cool';

export interface Lighting {
  /** Off by default — anatomy/skin isn't affected by lighting unless mum opts in. */
  enabled: boolean;
  key_angle: LightingKeyAngle;
  quality: LightingQuality;
  temperature: LightingTemperature;
  /** 0..1 — strength of the key light. */
  intensity: number;
}

export interface LimbalRing {
  /** Every realistic eye has a limbal ring — this is anatomy, defaults on. */
  enabled: boolean;
  /** 0..1 — strength of the dark annulus at the iris edge. */
  intensity: number;
}

export interface Catchlight {
  enabled: boolean;
  /** 0..1 horizontal position within the iris. */
  x: number;
  /** 0..1 vertical position within the iris. */
  y: number;
  /** 0..1 size relative to iris radius. */
  size: number;
  shape: CatchlightShape;
  /** CSS hex colour, e.g. "#ffffff". */
  color: string;
  /** 0..1 opacity / brightness. */
  intensity: number;
  /** 0..1 blur radius. */
  blur: number;
  /** Number of catchlight shapes, 1..3. */
  count: number;
  /** How multiple catchlights are arranged. */
  count_pattern: CatchlightCountPattern;
}

export interface ReflectedScene {
  enabled: boolean;
  preset: ReflectedScenePreset;
  /** 0..1 horizontal position within the iris. */
  x: number;
  /** 0..1 vertical position within the iris. */
  y: number;
  /** 0..1 size relative to iris radius. */
  size: number;
  /** 0..1 overlay opacity. */
  opacity: number;
  /** CSS hex tint applied over the scene, e.g. "#88aaff". */
  tint: string;
  /** Rotation in degrees, 0..360. */
  rotation: number;
  /** 0..1 — lens-like curvature distortion. */
  distortion: number;
}

export interface ColorTint {
  enabled: boolean;
  /** CSS hex colour, e.g. "#ffaaff". */
  color: string;
  /** 0..1 mix strength. */
  intensity: number;
}

/**
 * Eye Gallery v5 — every parameter is configurable in UI and via chat.
 * v5 adds three new top-level fields: `emotion`, `gaze_direction`,
 * `lighting`. All have safe defaults so v4 Parameters remain valid input.
 */
export interface Parameters {
  // ─── Base eye anatomy ────────────────────────────────────────────────
  /** CSS hex colour of the iris, e.g. "#5a8aa0". */
  iris_color: string;
  /** 0..1 — fibre / crypt textural complexity dial. */
  iris_detail: number;
  /** Structural iris type. */
  iris_pattern: IrisPattern;
  limbal_ring: LimbalRing;
  /** 0..1 — 2mm (constricted) to 8mm (dilated). */
  pupil_size: number;
  pupil_shape: PupilShape;
  /** CSS hex colour of the sclera (white of the eye), e.g. "#f4ece0". */
  sclera_tint: string;
  /** 0..1 — visible vein detail in the sclera. */
  sclera_veining: number;
  eyelid_shape: EyelidShape;
  /** 0..1 — depth of shadow cast by the lid onto the eye. */
  lid_shadow: number;
  /** 0..1 — lash density. */
  lash_density: number;
  /** 0..1 — lash length. */
  lash_length: number;
  lash_curve: LashCurve;

  // ─── Programmable reflections ───────────────────────────────────────
  catchlight: Catchlight;
  reflected_scene: ReflectedScene;

  // ─── Color grading ───────────────────────────────────────────────────
  color_tint: ColorTint;

  // ─── v5 — emotional / directional state ──────────────────────────────
  emotion: Emotion;
  gaze_direction: GazeDirection;
  lighting: Lighting;

  // ─── Technical / aesthetic ───────────────────────────────────────────
  aspect_ratio: AspectRatio;
  resolution: Resolution;
  /** Explore mode — 4× cost, 4× picks. Default 1. */
  n_variations: NVariations;
  /** 0..1 — how much skin surrounds the eye. */
  skin_visibility: number;
  /** 0..1 — skin bokeh intensity. */
  bokeh_strength: number;
}

// ─── Defaults (all "off" — mum opts in) ──────────────────────────────
// Matches the JSON block in ARCHITECTURE.md + the v5 backend defaults.
export const DEFAULT_PARAMETERS: Parameters = {
  iris_color: '#5a8aa0',
  iris_detail: 0.5,
  iris_pattern: 'radial',
  limbal_ring: { enabled: true, intensity: 0.5 },
  pupil_size: 0.4,
  pupil_shape: 'round',
  sclera_tint: '#f4ece0',
  sclera_veining: 0.3,
  eyelid_shape: 'open',
  lid_shadow: 0.4,
  lash_density: 0.5,
  lash_length: 0.5,
  lash_curve: 'subtle',
  catchlight: {
    enabled: false,
    x: 0.35,
    y: 0.3,
    size: 0.18,
    shape: 'round',
    color: '#ffffff',
    intensity: 0.9,
    blur: 0.25,
    count: 1,
    count_pattern: 'single',
  },
  reflected_scene: {
    enabled: false,
    preset: 'sky',
    x: 0.5,
    y: 0.5,
    size: 0.7,
    opacity: 0.4,
    tint: '#88aaff',
    rotation: 0,
    distortion: 0.3,
  },
  color_tint: {
    enabled: false,
    color: '#ffaaff',
    intensity: 0,
  },
  // v5 defaults (off / neutral)
  emotion: 'neutral',
  gaze_direction: 'centre',
  lighting: {
    enabled: false,
    key_angle: 'front-left',
    quality: 'soft',
    temperature: 'neutral',
    intensity: 0.5,
  },
  aspect_ratio: '4:3',
  resolution: '1K',
  n_variations: 1,
  skin_visibility: 0.4,
  bokeh_strength: 0.5,
};

// ─── Enum tables (used by Select controls — kept here so type & UI stay in sync) ──

export const IRIS_PATTERN_OPTIONS: { value: IrisPattern; label: string }[] = [
  { value: 'radial', label: 'Radial' },
  { value: 'cryptic', label: 'Cryptic' },
  { value: 'zonular', label: 'Zonular' },
  { value: 'mixed', label: 'Mixed' },
];

export const PUPIL_SHAPE_OPTIONS: { value: PupilShape; label: string }[] = [
  { value: 'round', label: 'Round' },
  { value: 'slightly-off-round', label: 'Slightly off-round' },
];

export const EYELID_SHAPE_OPTIONS: { value: EyelidShape; label: string }[] = [
  { value: 'open', label: 'Open' },
  { value: 'half', label: 'Half-closed' },
  { value: 'closed', label: 'Closed' },
];

export const LASH_CURVE_OPTIONS: { value: LashCurve; label: string }[] = [
  { value: 'straight', label: 'Straight' },
  { value: 'subtle', label: 'Subtle' },
  { value: 'dramatic', label: 'Dramatic' },
];

export const CATCHLIGHT_SHAPE_OPTIONS: { value: CatchlightShape; label: string }[] = [
  { value: 'round', label: 'Round' },
  { value: 'oval', label: 'Oval' },
  { value: 'square', label: 'Square' },
  { value: 'star', label: 'Star' },
];

export const CATCHLIGHT_COUNT_PATTERN_OPTIONS: {
  value: CatchlightCountPattern;
  label: string;
}[] = [
  { value: 'single', label: 'Single' },
  { value: 'paired', label: 'Paired' },
  { value: 'cluster', label: 'Cluster' },
];

export const REFLECTED_SCENE_PRESET_OPTIONS: {
  value: ReflectedScenePreset;
  label: string;
}[] = [
  { value: 'sky', label: 'Sky' },
  { value: 'fire', label: 'Fire' },
  { value: 'forest', label: 'Forest' },
  { value: 'water', label: 'Water' },
  { value: 'stars', label: 'Stars' },
  { value: 'city', label: 'City' },
  { value: 'abstract', label: 'Abstract' },
];

export const ASPECT_RATIO_OPTIONS: { value: AspectRatio; label: string }[] = [
  { value: '1:1', label: '1:1 (square)' },
  { value: '4:3', label: '4:3 (landscape)' },
  { value: '3:4', label: '3:4 (portrait)' },
  { value: '16:9', label: '16:9 (wide)' },
  { value: '9:16', label: '9:16 (tall)' },
];

export const RESOLUTION_OPTIONS: { value: Resolution; label: string }[] = [
  { value: '1K', label: '1K' },
  { value: '2K', label: '2K' },
  { value: '4K', label: '4K' },
];

export const EMOTION_OPTIONS: { value: Emotion; label: string }[] = [
  { value: 'neutral', label: 'Neutral' },
  { value: 'alert', label: 'Alert' },
  { value: 'menacing', label: 'Menacing' },
  { value: 'sad', label: 'Sad' },
  { value: 'sleepy', label: 'Sleepy' },
  { value: 'surprised', label: 'Surprised' },
  { value: 'contempt', label: 'Contempt' },
  { value: 'fierce', label: 'Fierce' },
  { value: 'tearful', label: 'Tearful' },
  { value: 'peaceful', label: 'Peaceful' },
  { value: 'haunted', label: 'Haunted' },
];

export const GAZE_DIRECTION_OPTIONS: { value: GazeDirection; label: string }[] = [
  { value: 'centre', label: 'Centre' },
  { value: 'left', label: 'Left' },
  { value: 'right', label: 'Right' },
  { value: 'up', label: 'Up' },
  { value: 'down', label: 'Down' },
  { value: 'up-left', label: 'Up-left' },
  { value: 'up-right', label: 'Up-right' },
  { value: 'down-left', label: 'Down-left' },
  { value: 'down-right', label: 'Down-right' },
];

export const LIGHTING_KEY_ANGLE_OPTIONS: {
  value: LightingKeyAngle;
  label: string;
}[] = [
  { value: 'front-left', label: 'Front-left' },
  { value: 'front-right', label: 'Front-right' },
  { value: 'side-left', label: 'Side-left' },
  { value: 'side-right', label: 'Side-right' },
  { value: 'back-left', label: 'Back-left' },
  { value: 'back-right', label: 'Back-right' },
  { value: 'above', label: 'Above' },
  { value: 'below', label: 'Below' },
  { value: 'ambient', label: 'Ambient (no key)' },
];

export const LIGHTING_QUALITY_OPTIONS: {
  value: LightingQuality;
  label: string;
}[] = [
  { value: 'soft', label: 'Soft' },
  { value: 'hard', label: 'Hard' },
];

export const LIGHTING_TEMPERATURE_OPTIONS: {
  value: LightingTemperature;
  label: string;
}[] = [
  { value: 'warm', label: 'Warm' },
  { value: 'neutral', label: 'Neutral' },
  { value: 'cool', label: 'Cool' },
];
